import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { LogOut, Clock, CheckCircle, XCircle, Calendar, MessageSquare, UserCheck, Building, ShieldCheck, DollarSign, Laptop, ClipboardList } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExitPortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [activeRequest, setActiveRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExitData();
  }, [impersonatedUser]);

  const fetchExitData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/exit/${impersonatedUser.employee_id}`,
        config
      );
      
      setActiveRequest(response.data.active_request || null);
    } catch (error) {
      console.error('Error fetching exit data:', error);
      toast.error('Unable to fetch exit data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getApprovalStatusBadge = (status) => {
    const configs = {
      'pending': { bg: 'bg-red-100', text: 'text-red-700', label: 'Pending' },
      'approved': { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' }
    };
    const config = configs[status] || configs['pending'];
    
    return (
      <Badge variant="outline" className={`${config.bg} ${config.text} text-xs`}>
        {config.label}
      </Badge>
    );
  };

  const getClearanceProgress = (clearance) => {
    if (!clearance) return { completed: 0, total: 0, percentage: 0 };
    let total = 0;
    let completed = 0;
    Object.values(clearance).forEach(dept => {
      dept.items?.forEach(item => {
        total++;
        if (item.status !== null) completed++;
      });
    });
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="exit-portal-embed">
      {activeRequest ? (
        <div className="space-y-6">
          {/* Exit Request Details */}
          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <LogOut className="w-5 h-5 text-red-600" />
                  Exit Request for {impersonatedUser.name}
                </span>
                <Badge className={activeRequest.status === 'completed' ? 'bg-green-600' : 'bg-orange-500'}>
                  {activeRequest.is_revoked ? 'Revoked' : activeRequest.status === 'completed' ? 'Completed' : 'Pending'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {activeRequest.is_revoked ? (
                <div className="bg-gray-100 p-4 rounded-lg text-center">
                  <p className="text-gray-600 font-medium">Exit request has been revoked</p>
                  <p className="text-sm text-gray-500 mt-1">Reason: {activeRequest.revocation_reason}</p>
                </div>
              ) : (
                <>
                  {/* Request Details */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase">Submission Date</p>
                      <p className="font-semibold flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(activeRequest.submission_date)}
                      </p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase">Last Working Day</p>
                      <p className="font-semibold text-red-700 flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-red-400" />
                        {formatDate(activeRequest.last_working_day)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase">Reason</p>
                      <p className="font-medium">{activeRequest.reason_for_leaving}</p>
                    </div>
                  </div>

                  {/* Parallel Approval Status Grid */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Department Approval Status
                    </h4>
                    <div className="grid grid-cols-5 gap-2">
                      {[
                        { key: 'rm', label: 'Manager', icon: UserCheck, status: activeRequest.rm_status, notes: activeRequest.rm_notes },
                        { key: 'hr', label: 'HR', icon: Building, status: activeRequest.hr_status, notes: activeRequest.hr_notes },
                        { key: 'admin', label: 'Admin', icon: ShieldCheck, status: activeRequest.admin_status, notes: activeRequest.admin_notes },
                        { key: 'payroll', label: 'Payroll', icon: DollarSign, status: activeRequest.payroll_status, notes: activeRequest.payroll_notes },
                        { key: 'it', label: 'IT', icon: Laptop, status: activeRequest.it_status, notes: activeRequest.it_notes }
                      ].map(dept => (
                        <div 
                          key={dept.key}
                          className={`p-2 rounded-lg border ${
                            dept.status === 'approved' ? 'bg-green-50 border-green-300' : 
                            dept.status === 'rejected' ? 'bg-red-50 border-red-300' : 
                            'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-center gap-1 mb-1">
                            <dept.icon className={`w-3 h-3 ${
                              dept.status === 'approved' ? 'text-green-600' : 
                              dept.status === 'rejected' ? 'text-red-600' : 
                              'text-red-500'
                            }`} />
                            <span className="text-xs font-medium">{dept.label}</span>
                          </div>
                          {getApprovalStatusBadge(dept.status)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Clearance Progress */}
                  {activeRequest.clearance && (
                    <div className="border-t pt-4 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-700 flex items-center gap-2 text-sm">
                          <ClipboardList className="w-4 h-4" />
                          Clearance Progress
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">
                            {getClearanceProgress(activeRequest.clearance).completed}/{getClearanceProgress(activeRequest.clearance).total}
                          </span>
                          <Progress value={getClearanceProgress(activeRequest.clearance).percentage} className="w-24 h-2" />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-12">
          <LogOut className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Active Exit Request</h3>
          <p className="text-gray-500">{impersonatedUser.name} has not submitted a resignation request.</p>
        </div>
      )}
    </div>
  );
};

export default ExitPortalEmbed;
